'use client'

import { useMemo, useState } from 'react'
import {
  EntityListShell,
  Pagination,
  SearchInput,
  Spinner,
  StatusChip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatAmount,
  formatDate,
} from '@rr/ui'
import { useCompanyQuotes } from '@/modules/platform/hooks/usePlatformCompanies'

const PAGE_SIZE = 20

export default function QuotesList({ companyId }: { companyId: string }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const filters = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
    }),
    [page, search],
  )

  const { data, isLoading, isError, error } = useCompanyQuotes(companyId, filters)
  const items: any[] = (data as any)?.data?.items ?? []
  const total: number = (data as any)?.data?.pagination?.total ?? items.length

  return (
    <EntityListShell
      toolbar={
        <SearchInput
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder="Search by quote # or client…"
          className="max-w-sm"
        />
      }
      empty={!isLoading && items.length === 0}
      emptyState={
        isError
          ? error instanceof Error
            ? error.message
            : 'Failed to load quotes.'
          : 'No quotes in this organization.'
      }
      pagination={
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onChange={setPage}
        />
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Quote #</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Origin → Destination</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((q) => {
              const origin = q.originAddress?.city ?? '—'
              const dest = q.destinationAddress?.city ?? '—'
              const rate = q.clientExpectedRate
              const currencyCode = q.currency?.code ?? ''
              return (
                <TableRow key={q.id}>
                  <TableCell className="font-medium text-foreground tnum">
                    {q.quoteNumber ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {q.client?.name ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {origin} → {dest}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={(q.status ?? 'draft') as any} />
                  </TableCell>
                  <TableCell className="text-right text-sm tnum">
                    {rate != null
                      ? formatAmount(Number(rate), { currency: currencyCode || 'USD' })
                      : '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(q.createdAt)}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </EntityListShell>
  )
}
